import { Activity, useState, type ComponentType, type ReactNode } from 'react';

interface Props {
  /** Current pathname. */
  active: string;
  /** The tab screens kept alive, by path. */
  screens: Record<string, ComponentType>;
  /** What shows on a path that is not a tab (the reco flow). */
  fallback: ReactNode;
}

/**
 * ios-tab-bar — each tab screen is mounted on its first visit, then kept: a
 * hidden one sits in a hidden `<Activity>`, which keeps its state and DOM
 * (scroll included) and runs its effect cleanups, so nothing it started —
 * listener, observer, timer, autoplay — keeps working in the background. Its
 * effects run again when it is shown. A tab never visited is never mounted.
 */
export default function KeepAliveTabs({ active, screens, fallback }: Props) {
  const isTab = active in screens;
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set());
  // Record the visit during render: the screen mounts in this same commit.
  if (isTab && !visited.has(active)) setVisited(new Set(visited).add(active));

  return (
    <>
      {Object.entries(screens).map(([path, Screen]) =>
        visited.has(path) || path === active ? (
          <Activity key={path} mode={path === active ? 'visible' : 'hidden'}>
            <div className="tw:h-full">
              <Screen />
            </div>
          </Activity>
        ) : null,
      )}
      {!isTab && fallback}
    </>
  );
}
